import moment from 'moment-timezone'
import _ from 'lodash'
import { timeZoneAbbrs } from '../../utils/momentTimezones'

moment.fn.zoneName = function () {
  const abbr = this.zoneAbbr()
  return timeZoneAbbrs[abbr] || abbr
}

const ONGOING_EVENT = 'in_progress'
const COMPLETED = 'completed'
const CANCELLED = 'cancelled'
const UPCOMING_EVENT = 'scheduled'
const FAILED = 'failed'
const STOPPED = 'stopped'
const CREATED = 'created'
const DELETED = 'deleted'
const SCHEDULING = 'scheduling'
const OPTEDOUT = 'OPTEDOUT'

const EVENT_TYPE = {
  BATTERY_DISCHARGE: 'Discharge Event',
  BATTERY_CHARGE: 'Charge Event',
  IDLE: 'Idle Event'
}

const EVENT_SUBTYPE = {
  Discharge_To_Load: 'Discharge to Load',
  Discharge_To_Load_Grid: 'Discharge to Load and Grid',
  Discharge_To_NetLoad: 'Discharge to Net Load',
  Charge_From_PV: 'Charge From PV',
  Charge_From_NetPV: 'Charge From Net PV',
  Charge_From_PV_Grid: 'Charge From PV and Grid',
  Idle: 'Idle'
}

const defaultSubType = {
  BATTERY_DISCHARGE: 'Discharge to Load and Grid',
  BATTERY_CHARGE: 'Charge From PV and Grid',
  IDLE: 'Idle'
}

const eventStatus = (status, eventDetails, timeZone) => {
  const startTime = _.get(eventDetails, 'start_date_time', '')
  let duration = {}

  if (status === UPCOMING_EVENT) {
    const minsDifference = moment(startTime).tz(timeZone).diff(moment().tz(timeZone), 'minutes')
    const days = (minsDifference / (24 * 60))
    const rdays = Math.floor(days)
    const hours = ((minsDifference - (rdays * 1440)) / 60)
    const rhours = Math.floor(hours)
    const minutes = (hours - rhours) * 60
    const rminutes = Math.round(minutes)
    duration = {
      days: rdays,
      mins: rminutes,
      hrs: rhours
    }
  }

  switch (status) {
    case STOPPED:
    case COMPLETED:
      return {
        label: 'Completed',
        color: '#7ACF38'
      }
    case CANCELLED:
      return {
        label: 'Cancelled',
        color: '#929699'
      }
    case ONGOING_EVENT:
      return {
        label: 'Ongoing',
        color: '#EDCD1A'
      }
    case FAILED:
      return {
        label: 'Failed',
        color: '#D82424'
      }
    default:
      return {
        label: `Upcoming - Event starts in${duration.days ? ` ${duration.days} ${duration.days > 1 ? `days` : 'day'}` : ' '}${duration.hrs ? ` ${duration.hrs} ${duration.hrs > 1 ? 'hours' : 'hour'},` : ' '} ${duration.mins} ${duration.mins > 1 ? 'minutes' : 'minute'}`,
        color: '#0438E3'
      }
  }
}

const eventHighLights = (data, timeZone, isSuperAdmin, isViewOnly) => {
  const startTime = moment(_.get(data, 'start_date_time', ''))
  const endTime = moment(_.get(data, 'end_date_time', ''))
  const duration = moment.duration(endTime.diff(startTime))
  return [
    {
      label: 'Event Start Time',
      value: _.get(data, 'start_date_time', '') ? moment(_.get(data, 'start_date_time', '')).tz(timeZone).format('MMMM DD, YYYY hh:mm A zz') : '--'
    },
    {
      label: 'Event Type',
      value: _.get(data, 'type', '') === '' ? '--' : EVENT_TYPE[_.get(data, 'type', '')]
    },
    {
      label: 'Event Duration',
      value: _.get(data, 'start_date_time', '') && _.get(data, 'end_date_time', '') ? `${Math.floor(duration.asHours())} hour(s) ${moment.utc(duration.asMilliseconds()).format("mm")} minute(s)` : '--'
    },
    {
      label: 'Event Sub type',
      value: !_.isNil(data.subtype) ? EVENT_SUBTYPE[data.subtype] : defaultSubType[data.type] || '-'
    },
    {
      label: 'Event End Time',
      value: _.get(data, 'end_date_time', '') ? moment(_.get(data, 'end_date_time', '')).tz(timeZone).format('MMMM DD, YYYY hh:mm A zz') : '--'
    },
    {
      label: 'Total Systems',
      value: _.get(data, 'systems', '--'),
      isLink: true
    },
    {
      label: 'Target SoC',
      value: !_.isNil(data.target_soc) ? `${data.target_soc}%` : data && data.type === 'BATTERY_DISCHARGE' ? '10%' : data && data.type === 'BATTERY_CHARGE' ? '100%' : '--'
    },
    (isSuperAdmin || isViewOnly) ? {
      label: 'Event Viewer',
      value: 'Event Information',
      isLink: true
    } : null,
  ]
}

const graphData = (response, eventDetails, timeZone) => {
  const start_date_time = _.get(eventDetails, 'start_date_time', '')
  const end_date_time = _.get(eventDetails, 'end_date_time', '')
  const eventType = _.get(eventDetails, 'type', '')
  let startTime = new Date(start_date_time).getTime() / 1000
  const endTime = new Date(end_date_time).getTime() / 1000
  let xDomain = []
  const totalPower = _.get(eventDetails, 'total_battery_power', 0)

  while (startTime < endTime) {
    xDomain.push(startTime)
    const nextIndex = moment.unix(startTime).tz(timeZone).add(15, 'minutes')
    startTime = new Date(nextIndex).getTime() / 1000
  }

  const data = xDomain.map((x, index) => {
    return {
      y: response[index] ? response[index] / 1000 : 0,
      x
    }
  })

  let maxYDomain = 0
  const max = (totalPower / 60) * 15
  if (max < 10) {
    maxYDomain = Math.ceil(max)
  } else if (max < 100) {
    maxYDomain = Math.ceil(max / 5) * 5
  } else {
    maxYDomain = Math.ceil(max / 10) * 10
  }

  return {
    title: eventType === 'BATTERY_DISCHARGE' ? 'Energy Discharged (kWh)' : 'Energy Charged (kWh)',
    showCrossHair: true,
    plot: {
      xType: "ordinal",
      stackBy: "y"
    },
    xAxis: {
      tickFormat: (val, index) => {
        const timestamp = moment.unix(val).tz(timeZone).format('hh:mm A')
        return timestamp
      }
    },
    yAxis: {
      yDomain: [0, maxYDomain]
    },
    graphsData: [
      {
        series: 'vertical', // type of bar series either vertical or horizontal
        color: '#6B7073', // color of the bars
        data,
        yDomain: [0, maxYDomain],
        opacity: "literal",
        colorType: "literal",
        stroke: "literal",
      }
    ]
  }
}

const systemsInfoTableHeadings = (classes, isSuperAdmin, isViewOnly) => ([
  {
    id: 'systemName',
    header: 'SYSTEM NAME',
    className: classes.tableCenter,
  },
  {
    id: 'systemId',
    header: 'SYSTEM ID',
    className: classes.tableCenter,
  },
  {
    id: 'scheduledEnergy',
    header: 'SCHEDULED ENERGY (kWh)',
    className: classes.tableCenter
  },
  {
    id: 'actualEnergy',
    header: 'ACTUAL ENERGY (kWh)',
    className: classes.tableCenter
  },
  {
    id: 'scheduledPower',
    header: 'SCHEDULED POWER (kW)',
    className: classes.tableCenter
  },
  {
    id: 'actualPower',
    header: 'ACTUAL POWER (kW)',
    className: classes.tableCenter
  },
  {
    id: 'eventStatus',
    header: 'EVENT STATUS',
  },
  (isSuperAdmin || isViewOnly) && {
    id: 'eventInfo',
    header: 'EVENT INFORMATION',
  } 
])

const getEventStatus = (status) => {
  switch (status) {
    case CREATED:
      return {
        color: '',
        text: ''
      }
    case CANCELLED:
      return {
        color: '#929699',
        text: 'Opted Out'
      }
    case UPCOMING_EVENT:
      return {
        color: '#0438E3',
        text: 'Upcoming'
      }
    case ONGOING_EVENT:
      return {
        color: '#EDCD1A',
        text: 'Ongoing'
      }
    case COMPLETED:
      return {
        color: '#7ACF38',
        text: 'Completed'
      }
    case DELETED:
      return {
        color: '#D82424',
        text: 'Deleted'
      }
    case STOPPED:
      return {
        color: '#D82424',
        text: 'Stopped'
      }
    case FAILED:
      return {
        color: '#D82424',
        text: 'Failed'
      }
    case SCHEDULING:
      return {
        color: '#FC943C',
        text: 'Scheduling'
      }
    case OPTEDOUT:
      return {
        color: '#929699',
        text: 'Opted out'
      }
    default:
      return {
        color: '#8E8E8E',
        text: 'Unknown Status'
      }
  }
}

export {
  eventStatus,
  eventHighLights,
  graphData,
  ONGOING_EVENT,
  UPCOMING_EVENT,
  STOPPED,
  systemsInfoTableHeadings,
  getEventStatus,
  defaultSubType
}